import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle,
  XCircle,
  Clock,
  Play,
  Pause,
  RotateCcw,
  Search,
  Settings,
  Trash2,
  Cpu,
  Monitor,
  Network,
  FileText,
  Eye,
  EyeOff,
  Download,
  ChevronRight,
  Shield,
  Lock,
  Unlock,
  Globe,
  Mail,
  Folder,
  HardDrive,
  Calculator,
  Maximize2,
  Volume2,
  Wifi,
  Battery,
  X
} from 'lucide-react';
import RealisticOSSimulator from './RealisticOSSimulator';
import { getChallengeById } from '../../api/games.api';
import { normalizePath as normalizePathUtil, getParentPath as getParentPathUtil, joinPath as joinPathUtil } from '../utils/pathUtils';

const EnhancedMalwareSimulator = ({
  challenge,
  onComplete,
  onExit,
  scenarios = [],
  estimatedTime = 40
}) => {
  const navigate = useNavigate();

  // Core state management
  const [steps, setSteps] = useState([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [userDecisions, setUserDecisions] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackContent, setFeedbackContent] = useState(null);
  const [simulationStarted, setSimulationStarted] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [progress, setProgress] = useState({ completed: 0, total: 0, accuracy: 100 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Challenge-specific state
  const [challengeData, setChallengeData] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showStepPrompt, setShowStepPrompt] = useState(false);
  const [stepPromptAnimVisible, setStepPromptAnimVisible] = useState(false);

  // Track processed events to prevent duplicate step completions
  const processedEventsRef = useRef(new Set());

  // Timer ref for cleanup
  const timerRef = useRef(null);

  // Initialize challenge data and steps
  useEffect(() => {
    const initializeChallenge = async () => {
      if (!challenge) return;

      setLoading(true);
      setError(null);

      try {
        console.log('🎮 EnhancedMalwareSimulator initializing with challenge:', challenge);

        // Use the challenge data passed from SecuritySimulationContainer
        const challengeId = challenge.id || challenge.challenge_id || challenge._id;

        // Process steps from the challenge data
        let rawSteps = [];
        if (challenge.steps && Array.isArray(challenge.steps)) {
          rawSteps = challenge.steps;
        } else if (challenge.content && Array.isArray(challenge.content)) {
          // Convert scenarios to steps format
          rawSteps = challenge.content.map((scenario, index) => ({
            id: `step-${index}`,
            title: scenario.title || `Step ${index + 1}`,
            description: scenario.description || '',
            type: scenario.type || 'simulation',
            content: scenario,
            order_index: index,
            time_limit: scenario.timeLimit || 300,
            points: scenario.points || 50,
            hints: scenario.hints || []
          }));
        }

        const processedSteps = processSteps(rawSteps);

        if (processedSteps.length > 0) {
          setSteps(processedSteps);
          setChallengeData(challenge);
          setProgress({ completed: 0, total: processedSteps.length, accuracy: 100 });

          // Build scenario for RealisticOSSimulator
          const builtScenario = buildScenarioFromSteps(processedSteps, challenge);
          console.log('📧 Built scenario emails check:', {
            totalEmails: builtScenario.emails?.length || 0,
            emailSubjects: builtScenario.emails?.map(e => e.subject) || [],
            emailIds: builtScenario.emails?.map(e => e.id) || [],
            scenarioKeys: Object.keys(builtScenario),
            scenarioEmailsArray: builtScenario.emails
          });
          setScenario(builtScenario);
          console.log('✅ EnhancedMalwareSimulator initialized successfully with steps:', processedSteps.length);
        } else {
          console.warn('⚠️ No challenge steps found, creating fallback scenario');
          // Create a fallback scenario for the specific challenge ID
          const fallbackSteps = createFallbackSteps(challengeId);
          setSteps(fallbackSteps);
          setChallengeData(challenge);
          setProgress({ completed: 0, total: fallbackSteps.length, accuracy: 100 });

          const builtScenario = buildScenarioFromSteps(fallbackSteps, challenge);
          setScenario(builtScenario);
        }
      } catch (err) {
        console.error('❌ Failed to initialize challenge:', err);
        setError('Failed to initialize challenge');
      } finally {
        setLoading(false);
      }
    };

    initializeChallenge();
  }, [challenge]);

  // Auto-open the step prompt when steps are ready and on each step change
  useEffect(() => {
    if (steps && steps.length > 0 && !isCompleted && !loading) {
      setShowStepPrompt(true);
    }
  }, [steps, currentStepIndex, isCompleted, loading]);

  // Animate step prompt enter on open / step change
  useEffect(() => {
    if (showStepPrompt) {
      setStepPromptAnimVisible(false);
      const t = setTimeout(() => setStepPromptAnimVisible(true), 20);
      return () => clearTimeout(t);
    }
  }, [showStepPrompt, currentStepIndex]);

  // Create fallback steps for specific challenge IDs
  const createFallbackSteps = useCallback((challengeId) => {
    if (challengeId === '0dc81efb-d795-48c6-a704-d19987e6ebdc') {
      // Disguised Email Attachment challenge
      return [
        {
          id: 'step-1',
          title: 'Investigate Suspicious Email',
          description: 'A user has forwarded a suspicious email claiming to be an overdue invoice. Open Outlook to find it.',
          type: 'simulation',
          content: { action: 'open_outlook', autoAdvance: true },
          timeLimit: 300,
          points: 10,
          hints: ['Phishing emails often create a false sense of urgency.'],
          orderIndex: 0
        },
        {
          id: 'step-2',
          title: 'Analyze Filename',
          description: 'The email has an attachment named `Invoice_Details_4815.pdf.exe`. What is the most significant red flag about this filename?',
          type: 'knowledge_check',
          content: {
            options: [
              'The filename includes numbers.',
              'The filename uses underscores.',
              'It uses a double extension (`.pdf.exe`) to hide the true file type.',
              'The filename is too long.'
            ],
            correctAnswer: 2,
            explanation: 'Windows executes files based on the FINAL extension. Attackers use icons and long filenames to trick users into thinking a file is a safe document (`.pdf`) when it is actually a dangerous executable (`.exe`).'
          },
          timeLimit: 300,
          points: 25,
          hints: ['Windows hides file extensions by default, making this trick very effective.'],
          orderIndex: 1
        },
        {
          id: 'step-3',
          title: 'Download for Analysis',
          description: 'Do you download the attachment name \'Invoice-Details_4815.pdf.exe\' file?',
          type: 'knowledge_check',
          content: {
            options: [
              'Yes, I will download it for analysis.',
              'No, I will not download suspicious attachments.',
              'I will scan it first before downloading.',
              'I will ask IT support before proceeding.'
            ],
            correctAnswer: 1,
            explanation: 'You should never download attachments from suspicious emails, even for analysis. This could lead to malware infection. Always use proper security tools and sandbox environments for analysis.'
          },
          timeLimit: 300,
          points: 15,
          hints: ['Never download or run attachments from untrusted emails on a real machine.'],
          orderIndex: 2
        },
        {
          id: 'step-4',
          title: 'Clean the Inbox',
          description: 'To complete the cleanup, go back to Outlook and delete the malicious email.',
          type: 'simulation',
          content: { action: 'delete_email', subject: 'URGENT: Overdue Invoice', autoAdvance: true },
          timeLimit: 300,
          points: 20,
          hints: ['Removing the email prevents others from falling for the same phish.'],
          orderIndex: 3
        }
      ];
    }

    // Default fallback
    return [
      {
        id: 'step-1',
        title: 'Security Challenge',
        description: 'Complete the security challenge',
        type: 'simulation',
        content: {},
        timeLimit: 300,
        points: 50,
        hints: [],
        orderIndex: 0
      }
    ];
  }, []);

  // Process raw steps from backend
  const processSteps = useCallback((rawSteps) => {
    return rawSteps
      .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
      .map(step => {
        // Parse content if it's a JSON string
        let content = {};
        if (step.content) {
          if (typeof step.content === 'string') {
            try {
              content = JSON.parse(step.content);
            } catch (e) {
              content = {};
            }
          } else if (typeof step.content === 'object') {
            content = step.content;
          }
        }

        return {
          id: step.id || step.step_id,
          title: step.title,
          description: step.description,
          type: step.type,
          content,
          timeLimit: step.time_limit,
          points: step.points,
          hints: step.hints || [],
          orderIndex: step.order_index || 0
        };
      });
  }, []);

  // Build scenario for RealisticOSSimulator
  const buildScenarioFromSteps = useCallback((processedSteps, challengeData) => {
    const scenario = {
      title: challengeData.title || challenge?.title,
      description: challengeData.description || challenge?.description,
      steps: processedSteps,
      initialNotifications: [],
      backgroundProcesses: [],
      fileSystem: [],
      emails: [],
      networkFlows: []
    };

    // Add challenge-specific elements based on challenge type
    const challengeId = challengeData.id || challenge?.id;

    // Add initial notification
    scenario.initialNotifications.push({
      id: 'challenge-start',
      type: 'info',
      title: 'Challenge Started',
      message: `Starting ${scenario.title || 'Security Challenge'}`
    });

    try {
      // processedSteps is expected to be an array (already normalized by processSteps)
      (processedSteps || []).forEach((step, stepIdx) => {
        const content = step.content || {};
        const initialPath = content.initial_path || content.initialPath || '';

        // helper: normalize common path typos (non-invasive)
        const normalizePath = (p) => {
          if (!p || typeof p !== 'string') return p;
          // Fix a common typo (Aderson -> Anderson) seen in content payloads
          // Add other corrections if you discover more typos in DB exports
          return p.replace('John Aderson', 'John Anderson');
        };

        // helper: ensure path points to a directory (not trailing slash weirdness)
        const normalizeDir = (p) => {
          if (!p) return p;
          // Use the centralized pathUtils normalizePath which handles Windows paths correctly
          let np = normalizePathUtil(p.trim());
          // Keep Windows-style paths (RealisticOSSimulator expects Windows paths)
          // Remove trailing slashes for directories
          if (np && np.length > 3 && np.endsWith('\\')) {
            np = np.slice(0, -1);
          }
          return np;
        };

        // 1) Merge explicit fileSystem arrays defined in step.content
        if (Array.isArray(content.fileSystem) && content.fileSystem.length > 0) {
          content.fileSystem.forEach((f, fIdx) => {
            // Normalize and ensure required fields
            const fileId = f.id || `${challengeId || 'chg'}-s${stepIdx}-f${fIdx}-${(f.name || '').replace(/\s+/g, '_')}`;
            // Normalize the path using centralized pathUtils
            const normalizedPath = normalizePathUtil(f.path || '/');
            const fileObj = {
              id: fileId,
              name: f.name || 'unknown.file',
              path: normalizedPath, // Use normalized Windows path
              type: f.type || 'file',
              isSuspicious: !!f.isSuspicious || !!f.suspicious,
              size: f.size || f.sizeBytes || '1 KB',
              modified: f.modified || f.modifiedAt || null,
              description: f.description || f.title || '',
              // preserve extras (hash, signature, processConfig etc.)
              ...f
            };

            // Avoid duplicate IDs
            if (!scenario.fileSystem.find(x => x.id === fileObj.id)) {
              scenario.fileSystem.push(fileObj);
            }
          });
        }

        // 1b) Also handle content.scenario.fileSystem (nested scenario object)
        if (content.scenario && Array.isArray(content.scenario.fileSystem) && content.scenario.fileSystem.length > 0) {
          // First, collect backgroundProcesses to match with files
          const backgroundProcesses = Array.isArray(content.scenario.backgroundProcesses) ? content.scenario.backgroundProcesses : [];

          content.scenario.fileSystem.forEach((f, fIdx) => {
            // Normalize and ensure required fields
            const fileId = f.id || `${challengeId || 'chg'}-s${stepIdx}-scenario-f${fIdx}-${(f.name || '').replace(/\s+/g, '_')}`;

            // Normalize the path - fix "test.user" to "John Anderson"
            let pathToNormalize = f.path || '/';
            // Replace test.user with John Anderson before normalization
            pathToNormalize = pathToNormalize.replace(/test\.user/gi, 'John Anderson');
            const normalizedPath = normalizePathUtil(pathToNormalize);

            // Try to find matching backgroundProcess by cmdline
            let matchingProcessConfig = f.processConfig || null;
            if (!matchingProcessConfig && backgroundProcesses.length > 0) {
              // Look for a backgroundProcess whose cmdline matches this file's path
              const filePathLower = normalizedPath.toLowerCase();
              const fileNameLower = (f.name || '').toLowerCase();

              const matchingProcess = backgroundProcesses.find(proc => {
                if (!proc.cmdline) return false;
                const cmdlineLower = proc.cmdline.toLowerCase();
                // Check if cmdline contains the file path or filename
                return cmdlineLower.includes(filePathLower) ||
                  cmdlineLower.includes(fileNameLower) ||
                  (fileNameLower.endsWith('.exe') && cmdlineLower.includes(fileNameLower.replace('.exe', '')));
              });

              if (matchingProcess) {
                // Convert backgroundProcess to processConfig format
                matchingProcessConfig = {
                  name: matchingProcess.name || fileNameLower.replace('.exe', '') + '.exe',
                  cpu: matchingProcess.cpu || { mean: 0, jitter: 0 },
                  memory: matchingProcess.memory || { mean: 0, jitter: 0 },
                  disk: matchingProcess.disk || { mean: 0, jitter: 0 },
                  network: matchingProcess.network || { mean: 0, jitter: 0 },
                  isRogue: matchingProcess.isRogue !== false,
                  cmdline: matchingProcess.cmdline,
                  description: matchingProcess.description
                };
              }
            }

            const fileObj = {
              id: fileId,
              name: f.name || 'unknown.file',
              path: normalizedPath, // Use normalized Windows path
              parentPath: getParentPathUtil(normalizedPath), // Set parentPath for proper filtering
              type: f.type || 'file',
              isSuspicious: !!f.isSuspicious || !!f.suspicious,
              size: f.size || f.sizeBytes || '1 KB',
              modified: f.modified || f.modifiedAt || null,
              description: f.description || f.title || '',
              processConfig: matchingProcessConfig, // Attach processConfig from backgroundProcesses
              // preserve extras (hash, signature, etc.)
              ...f
            };

            // Avoid duplicate IDs
            if (!scenario.fileSystem.find(x => x.id === fileObj.id)) {
              scenario.fileSystem.push(fileObj);
            }
          });

          // Also add backgroundProcesses to scenario.backgroundProcesses
          backgroundProcesses.forEach((proc, pIdx) => {
            const procId = proc.id || `${challengeId || 'chg'}-s${stepIdx}-bgproc-${pIdx}`;
            const procObj = {
              id: procId,
              name: proc.name || 'unknown.exe',
              type: 'background',
              cpu: proc.cpu || { mean: 0, jitter: 0 },
              memory: proc.memory || { mean: 0, jitter: 0 },
              disk: proc.disk || { mean: 0, jitter: 0 },
              network: proc.network || { mean: 0, jitter: 0 },
              isRogue: proc.isRogue !== false,
              cmdline: proc.cmdline,
              description: proc.description,
              spawnTime: proc.spawnTime,
              ...proc
            };

            if (!scenario.backgroundProcesses.find(x => x.id === procObj.id)) {
              scenario.backgroundProcesses.push(procObj);
            }
          });
        }

        // 2) Merge emails and their attachments (attachments become files in Downloads by default)
        if (Array.isArray(content.emails) && content.emails.length > 0) {
          content.emails.forEach((email, eIdx) => {
            // add email to scenario.emails if not already present (optional)
            const emailId = email.id || `${challengeId || 'chg'}-s${stepIdx}-email${eIdx}`;
            if (!scenario.emails.find(e => e.id === emailId)) {
              scenario.emails.push({
                id: emailId,
                subject: email.subject || email.title || 'No subject',
                from: email.from || 'unknown@domain',
                body: email.body || email.html || '',
                attachments: Array.isArray(email.attachments) ? email.attachments.map((a, ai) => ({
                  id: a.id || `${emailId}-att${ai}`,
                  name: a.name || 'attachment.bin',
                  description: a.description || '',
                  size: a.size || '1 KB',
                  type: a.type || 'file',
                  isMalicious: !!a.isMalicious || !!a.isSuspicious,
                  processConfig: a.processConfig || null,
                  path: a.path || '/Users/User/Downloads'
                })) : []
              });
            }

            // Add attachments to scenario.fileSystem so they appear in Explorer (Downloads)
            const attachments = email.attachments || [];
            attachments.forEach((att, aIdx) => {
              const attId = att.id || `${challengeId || 'chg'}-s${stepIdx}-email${eIdx}-att${aIdx}-${(att.name || '').replace(/\s+/g, '_')}`;
              // Normalize the path using centralized pathUtils
              const normalizedAttPath = normalizePathUtil(att.path || '/Users/User/Downloads');
              const attObj = {
                id: attId,
                name: att.name || 'attachment.bin',
                path: normalizedAttPath, // Use normalized Windows path
                type: att.type || 'file',
                isSuspicious: !!att.isMalicious || !!att.isSuspicious,
                size: att.size || '1 KB',
                description: att.description || '',
                // keep processConfig so executing it spawns processes as intended
                processConfig: att.processConfig || null,
                ...att
              };
              if (!scenario.fileSystem.find(x => x.id === attObj.id)) {
                scenario.fileSystem.push(attObj);
              }
            });
          });
        }

        // 3) If the step content exposes a "downloads" array (alternate schema), merge that too
        if (Array.isArray(content.downloads) && content.downloads.length > 0) {
          content.downloads.forEach((d, dIdx) => {
            const dId = d.id || `${challengeId || 'chg'}-s${stepIdx}-dl${dIdx}-${(d.name || '').replace(/\s+/g, '_')}`;
            // Normalize the path using centralized pathUtils
            const normalizedDlPath = normalizePathUtil(d.path || '/Users/User/Downloads');
            const dlObj = {
              id: dId,
              name: d.name || 'download.bin',
              path: normalizedDlPath, // Use normalized Windows path
              type: d.type || 'file',
              isSuspicious: !!d.isMalicious || !!d.isSuspicious,
              size: d.size || '1 KB',
              description: d.description || '',
              processConfig: d.processConfig || null,
              ...d
            };
            if (!scenario.fileSystem.find(x => x.id === dlObj.id)) {
              scenario.fileSystem.push(dlObj);
            }
          });
        }

        // NEW: handle content.scenarioFiles (used in several DB rows; includes ADS entries)
        if (Array.isArray(content.scenarioFiles) && content.scenarioFiles.length > 0) {
          content.scenarioFiles.forEach((sf, sIdx) => {
            // compute path: prefer sf.path, fallback to content.initial_path, fallback to step.initial_path
            let sfPath = sf.path || content.path || initialPath || '/';
            sfPath = normalizeDir(sfPath);

            // If ADS stream uses root or '/', place it in same folder as parent file (if parent exists)
            // Try to find a visible parent in this list: same filename before ':' or same base name
            if (sf.type === 'ads_stream' && (!sf.path || sf.path === '/' || sf.path === '')) {
              // look for a parent in the same scenarioFiles array
              const baseName = (sf.name || '').split(':')[0];
              const parent = (content.scenarioFiles || []).find(x => x.name === baseName || x.name === baseName || (x.name && x.name.split(':')[0] === baseName));
              if (parent && parent.path) {
                sfPath = normalizeDir(parent.path);
              } else if (initialPath) {
                sfPath = normalizeDir(initialPath);
              }
            }

            const fileId = sf.id || `${challengeId || 'chg'}-s${stepIdx}-scf${sIdx}-${(sf.name || '').replace(/\s+/g, '_')}`;

            // Determine the correct parentPath and full path
            // If sfPath is a directory path (doesn't end with filename), use it as parentPath
            // Otherwise, extract parentPath from the full path
            const normalizedPath = normalizePathUtil(sfPath);
            const fileName = sf.name || sf.filename || 'unknown.file';
            const isDirectoryPath = !normalizedPath.endsWith(fileName);

            let parentPath, fullPath;
            if (isDirectoryPath) {
              // sfPath is the directory, so parentPath is sfPath, fullPath includes filename
              parentPath = normalizedPath;
              // Use joinPath to properly construct the full path
              fullPath = joinPathUtil(parentPath, fileName);
            } else {
              // sfPath already includes filename, extract parentPath
              parentPath = getParentPathUtil(normalizedPath);
              fullPath = normalizedPath;
            }

            const fileObj = {
              id: fileId,
              name: fileName,
              path: fullPath, // Full path including filename
              parentPath: parentPath, // Directory path for filtering
              type: sf.type || 'file',
              isSuspicious: !!sf.isSuspicious || !!sf.isMalicious,
              size: sf.size || '1 KB',
              modified: sf.modified || sf.modifiedAt || null,
              description: sf.description || '',
              // preserve payload info / processConfig for ADS payloads
              payload_type: sf.payload_type || sf.payloadType || null,
              payload_label: sf.payload_label || sf.payloadLabel || null,
              processConfig: sf.processConfig || sf.process_config || sf.process || null,
              isHidden: !!sf.isHidden,
              // copy any other fields the DB has
              ...sf
            };

            // If this is an ads stream, ensure the explorer also has the visible parent file (if defined)
            if (fileObj.type === 'ads_stream') {
              // ensure parent visible file is present (same name before ':')
              const parentName = (fileObj.name || '').split(':')[0];
              if (parentName && !scenario.fileSystem.find(f => f.name === parentName && f.path === fileObj.path)) {
                // Add a visible placeholder file if not already present
                const parentId = `${challengeId || 'chg'}-s${stepIdx}-parent-${parentName.replace(/\s+/g, '_')}`;
                // Ensure parent file has correct parentPath
                const parentFullPath = joinPathUtil(fileObj.parentPath, parentName);
                scenario.fileSystem.push({
                  id: parentId,
                  name: parentName,
                  path: parentFullPath,
                  parentPath: fileObj.parentPath,
                  type: 'file',
                  size: sf.visibleSize || '1 KB',
                  isSuspicious: false,
                  description: 'Visible host file for ADS (placeholder)'
                });
              }
            }

            // Push file object if not duplicate id
            if (!scenario.fileSystem.find(x => x.id === fileObj.id)) {
              scenario.fileSystem.push(fileObj);
            }
          });
        }

        // Extract network flows from step content
        if (Array.isArray(content.networkFlows) && content.networkFlows.length > 0) {
          content.networkFlows.forEach((flow, fIdx) => {
            const flowId = flow.id || `${challengeId || 'chg'}-s${stepIdx}-flow${fIdx}`;
            const flowObj = {
              id: flowId,
              src: flow.src || flow.source || '0.0.0.0',
              dst: flow.dst || flow.destination || '0.0.0.0',
              protocol: flow.protocol || 'TCP',
              bytes: flow.bytes || flow.size || 0,
              isSuspicious: !!flow.isSuspicious || !!flow.suspicious,
              note: flow.note || flow.description || '',
              firstSeen: flow.firstSeen || flow.timestamp || new Date().toISOString(),
              ...flow
            };
            if (!scenario.networkFlows.find(x => x.id === flowObj.id)) {
              scenario.networkFlows.push(flowObj);
            }
          });
        }

        // Extract network flows from content.scenario.networkFlows (nested scenario object)
        if (content.scenario && Array.isArray(content.scenario.networkFlows) && content.scenario.networkFlows.length > 0) {
          content.scenario.networkFlows.forEach((flow, fIdx) => {
            const flowId = flow.id || `${challengeId || 'chg'}-s${stepIdx}-scenario-flow${fIdx}`;
            const flowObj = {
              id: flowId,
              src: flow.src || flow.source || '0.0.0.0',
              dst: flow.dst || flow.destination || '0.0.0.0',
              protocol: flow.protocol || 'TCP',
              bytes: flow.bytes || flow.size || 0,
              isSuspicious: !!flow.isSuspicious || !!flow.suspicious,
              note: flow.note || flow.description || '',
              firstSeen: flow.firstSeen || flow.timestamp || new Date().toISOString(),
              uri: flow.uri,
              ...flow
            };
            if (!scenario.networkFlows.find(x => x.id === flowObj.id)) {
              scenario.networkFlows.push(flowObj);
            }
          });
        }

      });
    } catch (mergeErr) {
      // Non-fatal: log and continue — do not interrupt existing logic
      console.warn('Could not merge step-level fileSystem into scenario (non-fatal):', mergeErr);
    }

    // Merge challenge-level scenario config from metadata (NEW - Database-driven)
    try {
      // Handle metadata - it might be a JSON string or already an object
      let metadata = challengeData?.metadata;
      if (metadata && typeof metadata === 'string') {
        try {
          metadata = JSON.parse(metadata);
        } catch (e) {
          console.warn('[EnhancedMalware] Failed to parse metadata as JSON:', e);
          metadata = null;
        }
      }

      console.log('[EnhancedMalware] Checking metadata:', {
        challengeId,
        hasChallengeData: !!challengeData,
        hasMetadata: !!metadata,
        metadataType: metadata ? typeof metadata : 'none',
        metadataKeys: metadata ? Object.keys(metadata) : [],
        hasScenario: !!(metadata && metadata.scenario),
        scenarioKeys: metadata?.scenario ? Object.keys(metadata.scenario) : [],
        emailsInScenario: metadata?.scenario?.emails ? metadata.scenario.emails.length : 0
      });

      if (metadata && metadata.scenario) {
        const metadataScenario = metadata.scenario;

        console.log('[EnhancedMalware] Loading scenario config from challenge metadata:', {
          challengeId,
          hasBackgroundProcesses: !!metadataScenario.backgroundProcesses,
          hasFileSystem: !!metadataScenario.fileSystem,
          hasNetworkFlows: !!metadataScenario.networkFlows,
          hasEmails: !!metadataScenario.emails,
          emailsCount: Array.isArray(metadataScenario.emails) ? metadataScenario.emails.length : 0,
          emailsData: metadataScenario.emails
        });

        // Merge background processes from metadata
        if (Array.isArray(metadataScenario.backgroundProcesses)) {
          metadataScenario.backgroundProcesses.forEach(proc => {
            // Ensure process has required structure
            const processObj = {
              id: proc.id || `bg-${proc.name || 'unknown'}-${Date.now()}`,
              name: proc.name || 'unknown.exe',
              type: proc.type || 'background',
              cpu: proc.cpu || { mean: 0, jitter: 0 },
              memory: proc.memory || { mean: 0, jitter: 0 },
              disk: proc.disk || { mean: 0, jitter: 0 },
              network: proc.network || { mean: 0, jitter: 0 },
              isRogue: proc.isRogue !== false,
              cmdline: proc.cmdline || null,
              description: proc.description || null,
              ...proc
            };

            // Avoid duplicates
            if (!scenario.backgroundProcesses.find(p => p.id === processObj.id)) {
              scenario.backgroundProcesses.push(processObj);
              console.log('[EnhancedMalware] Added background process from metadata:', processObj.name);
            }
          });
        }

        // Merge file system entries from metadata
        if (Array.isArray(metadataScenario.fileSystem)) {
          metadataScenario.fileSystem.forEach(file => {
            // Normalize path using centralized pathUtils
            const normalizedPath = normalizePathUtil(file.path || '/');
            const fileObj = {
              id: file.id || `${challengeId || 'chg'}-meta-file-${(file.name || '').replace(/\s+/g, '_')}`,
              name: file.name || 'unknown.file',
              path: normalizedPath,
              parentPath: getParentPathUtil(normalizedPath),
              type: file.type || 'file',
              isSuspicious: !!file.isSuspicious || !!file.suspicious,
              size: file.size || '1 KB',
              modified: file.modified || new Date().toLocaleDateString(),
              description: file.description || '',
              hash: file.hash || null,
              signature: file.signature || null,
              processConfig: file.processConfig || null,
              ...file
            };

            // Avoid duplicates
            if (!scenario.fileSystem.find(f => f.id === fileObj.id)) {
              scenario.fileSystem.push(fileObj);
              console.log('[EnhancedMalware] Added file from metadata:', fileObj.name);
            }
          });
        }

        // Merge network flows from metadata
        if (Array.isArray(metadataScenario.networkFlows)) {
          metadataScenario.networkFlows.forEach(flow => {
            const flowObj = {
              id: flow.id || `${challengeId || 'chg'}-meta-flow-${Date.now()}`,
              src: flow.src || flow.source || '0.0.0.0',
              dst: flow.dst || flow.destination || '0.0.0.0',
              protocol: flow.protocol || 'TCP',
              bytes: flow.bytes || flow.size || 0,
              isSuspicious: !!flow.isSuspicious || !!flow.suspicious,
              note: flow.note || flow.description || '',
              firstSeen: flow.firstSeen || flow.timestamp || new Date().toISOString(),
              uri: flow.uri || null,
              ...flow
            };

            // Avoid duplicates
            if (!scenario.networkFlows.find(f => f.id === flowObj.id)) {
              scenario.networkFlows.push(flowObj);
              console.log('[EnhancedMalware] Added network flow from metadata:', flowObj.note);
            }
          });
        }

        // Merge emails from metadata
        if (Array.isArray(metadataScenario.emails)) {
          console.log('[EnhancedMalware] Processing emails from metadata:', {
            emailsCount: metadataScenario.emails.length,
            emails: metadataScenario.emails
          });

          metadataScenario.emails.forEach((email, emailIdx) => {
            console.log(`[EnhancedMalware] Processing email ${emailIdx}:`, email);

            const emailObj = {
              id: email.id || `${challengeId || 'chg'}-meta-email-${Date.now()}-${emailIdx}`,
              from: email.from || 'unknown@domain',
              subject: email.subject || email.title || 'No subject',
              date: email.date || 'Today',
              preview: email.preview || '',
              body: email.body || email.html || '',
              read: email.read !== undefined ? email.read : false,
              attachments: Array.isArray(email.attachments)
                ? email.attachments.map((att, ai) => ({
                  id: att.id || `${email.id || 'email'}-att${ai}`,
                  name: att.name || 'attachment.bin',
                  description: att.description || '',
                  size: att.size || '1 KB',
                  type: att.type || 'file',
                  isMalicious: !!att.isMalicious || !!att.isSuspicious,
                  processConfig: att.processConfig || null,
                  path: att.path || 'C:\\Users\\John Anderson\\Downloads'
                }))
                : [],
              ...email
            };

            // Avoid duplicates
            const existingEmail = scenario.emails.find(e => e.id === emailObj.id);
            if (!existingEmail) {
              scenario.emails.push(emailObj);
              console.log('[EnhancedMalware] ✅ Added email from metadata:', {
                id: emailObj.id,
                subject: emailObj.subject,
                from: emailObj.from,
                attachmentsCount: emailObj.attachments.length,
                totalEmailsInScenario: scenario.emails.length
              });

              // Also add attachments to file system
              emailObj.attachments.forEach((att, aIdx) => {
                const attId = att.id || `${challengeId || 'chg'}-meta-email-${emailObj.id}-att${aIdx}-${(att.name || '').replace(/\s+/g, '_')}`;
                const normalizedAttPath = normalizePathUtil(att.path || 'C:\\Users\\John Anderson\\Downloads');
                const attObj = {
                  id: attId,
                  name: att.name || 'attachment.bin',
                  path: normalizedAttPath,
                  parentPath: getParentPathUtil(normalizedAttPath),
                  type: att.type || 'file',
                  isSuspicious: !!att.isMalicious || !!att.isSuspicious,
                  size: att.size || '1 KB',
                  description: att.description || '',
                  processConfig: att.processConfig || null,
                  ...att
                };

                if (!scenario.fileSystem.find(x => x.id === attObj.id)) {
                  scenario.fileSystem.push(attObj);
                }
              });
            }
          });
        }
      }
    } catch (metaErr) {
      console.warn('[EnhancedMalware] Failed to merge metadata scenario (non-fatal):', metaErr);
    }

    // Final log to verify emails are in scenario
    console.log('[EnhancedMalware] Final scenario summary:', {
      challengeId,
      totalEmails: scenario.emails.length,
      totalFiles: scenario.fileSystem.length,
      totalProcesses: scenario.backgroundProcesses.length,
      totalNetworkFlows: scenario.networkFlows.length,
      emailSubjects: scenario.emails.map(e => e.subject)
    });

    return scenario;
  }, [challenge]);

  // Timer for challenge duration
  useEffect(() => {
    if (simulationStarted && !showFeedback && !isCompleted) {
      timerRef.current = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [simulationStarted, showFeedback, isCompleted]);

  // Complete challenge
  const completeChallenge = useCallback(() => {
    setIsCompleted(true);
    const correctDecisions = Object.values(userDecisions).filter(d => d.correct).length;
    const summary = {
      correct: correctDecisions,
      total: steps.length,
      score: Math.round((correctDecisions / Math.max(1, steps.length)) * 100),
      timeElapsed,
      decisions: userDecisions,
      accuracy: progress.accuracy
    };

    if (onComplete) {
      onComplete(summary);
    }
  }, [userDecisions, steps.length, timeElapsed, progress.accuracy, onComplete]);

  const handleStepComplete = useCallback((result = {}) => {
    const stepId = steps[currentStepIndex]?.id || `step-${currentStepIndex}`;
    const eventKey = `${stepId}-${result.action || result.event || 'complete'}`;

    // Prevent duplicate step completions for the same step/action
    if (processedEventsRef.current.has(eventKey)) {
      console.log("⚠️ Duplicate step completion prevented:", eventKey);
      return;
    }

    processedEventsRef.current.add(eventKey);
    console.log("✅ Step completed:", currentStepIndex, result);

    // Mark step completion
    setUserDecisions(prev => (prev || {}));
    setUserDecisions(prev => ({
      ...prev,
      [stepId]: {
        correct: true,
        data: result
      }
    }));

    // Go to next step or finish
    if (currentStepIndex < steps.length - 1) {
      // Smoothly advance to next step, delay opening modal
      setCurrentStepIndex(prev => prev + 1);
      setTimeout(() => setShowStepPrompt(true), 200); // small delay ensures new step is ready
    } else {
      const correctCount = Object.values(userDecisions).filter(d => d.correct).length + 1;
      const summary = {
        correct: correctCount,
        total: steps.length,
        score: Math.round((correctCount / steps.length) * 100),
        timeElapsed,
        decisions: userDecisions
      };
      setIsCompleted(true);
      if (onComplete) onComplete(summary);
    }
  }, [currentStepIndex, steps, userDecisions, timeElapsed, onComplete, setShowStepPrompt]);

  // Handle user actions
  const handleUserAction = useCallback((action, stepIdOrEvent, payload) => {
    /**
     * This function is intentionally flexible:
     * - original callers used handleUserAction(action, stepId)
     * - other callers (OS events) pass handleUserAction(action, stepEvent, payload)
     *
     * We'll detect whether the second argument is a stepId (exists in steps)
     * or an event identifier (not a step id), and assign variables accordingly.
     */

    const currentStep = steps[currentStepIndex];
    if (!currentStep) return;

    // Determine if second param is a real step id or an event string
    let stepIdToUse = currentStep.id;
    let stepEvent = null;

    if (stepIdOrEvent) {
      // if it matches an existing step id, treat it as stepId
      const foundStep = (steps || []).find(s => s.id === stepIdOrEvent);
      if (foundStep) {
        stepIdToUse = stepIdOrEvent;
      } else {
        // otherwise treat as an event name
        stepEvent = stepIdOrEvent;
      }
    }

    // If a payload was passed as third arg, use it for further checks (e.g., file object)
    const dataPayload = payload;

    // Determine if action is correct based on step type and content
    // STRICT VALIDATION: Only the expected action/event should be marked as correct
    // BUT: Allow prerequisite actions (e.g., opening File Explorer before quarantining)
    let isCorrect = false;
    let shouldProgress = false;

    if (currentStep.type === 'simulation') {
      const expectedAction = currentStep.content?.action ||
        currentStep.content?.correctAction ||
        currentStep.content?.onUserAction;
      const expectedEvent = currentStep.content?.expectedEvent || currentStep.content?.event;
      const autoAdvance = currentStep.content?.autoAdvance;

      console.log('[EnhancedMalware] Step validation:', {
        stepTitle: currentStep.title,
        action,
        stepEvent,
        expectedAction,
        expectedEvent,
        content: currentStep.content
      });

      // Check if step expects a command (toolCommand or toolRun)
      const expectsCommand = currentStep.content?.toolRun || currentStep.content?.toolCommand;

      // Check if this is a containment step
      const isContainmentStep = expectedAction === 'contain_and_collect' ||
        expectedAction === 'contain_recover' ||
        currentStep.title?.toLowerCase().includes('containment');

      // Define prerequisite actions - these are allowed but don't progress the step
      const prerequisiteActions = {
        'quarantine_file': ['open_file_explorer', 'file_explorer_opened'],
        'end_task': ['open_taskmgr', 'taskmgr_opened', 'task_manager_opened'],
        'execute_file': ['open_file_explorer', 'file_explorer_opened'],
        'extract_and_execute': ['open_file_explorer', 'file_explorer_opened'], // Allow opening File Explorer before extracting/executing
        'delete_email': ['open_outlook', 'outlook_opened'],
        'enable_macros': ['open_file_explorer', 'file_explorer_opened'],
        'contain_and_collect': ['open_taskmgr', 'taskmgr_opened', 'task_manager_opened', 'end_task']
      };

      // Check if this is a command execution and step expects a command
      if (action === 'run_command' && expectsCommand) {
        // Check if the command matched (from payload)
        if (dataPayload?.matchesExpected) {
          isCorrect = true;
          shouldProgress = true;
          console.log('[EnhancedMalware] Command action recognized and matched for toolCommand step');
        } else {
          isCorrect = false;
          shouldProgress = false;
          console.log('[EnhancedMalware] Command action recognized but did not match expected command');
        }
      }
      // Special handling: Steps with zipContents discoveries can be completed by listing ZIP with a command
      // even if the expected action is "download_attachment"
      else if (action === 'run_command' && currentStep.content?.discoveries?.zipContents &&
        dataPayload?.matchesExpected && expectedAction === 'download_attachment') {
        // User listed ZIP contents with a command - this completes the step
        isCorrect = true;
        shouldProgress = autoAdvance !== false;
        console.log('[EnhancedMalware] ZIP listing command recognized for download_attachment step with zipContents');
      }
      // Special handling for containment steps - they require multiple actions in sequence
      else if (isContainmentStep) {
        // For containment steps, both end_task (terminate processes) and quarantine_file are valid
        // The step completes after quarantining files
        if (action === 'end_task' || stepEvent === 'process_terminated' || stepEvent === 'taskmgr_process_ended') {
          isCorrect = true;
          shouldProgress = false; // Don't progress yet - need to quarantine files too
          console.log(`✅ Process termination recognized for containment step - now quarantine files`);
        } else if (action === 'quarantine_file' || stepEvent === 'file_quarantined') {
          // After processes are terminated, quarantine completes the step
          isCorrect = true;
          shouldProgress = true; // Complete the step after quarantining
          console.log(`✅ File quarantine recognized - containment step complete`);
        } else if (action === 'open_taskmgr' || stepEvent === 'taskmgr_opened') {
          // Opening Task Manager is a prerequisite
          isCorrect = true;
          shouldProgress = false;
          console.log(`ℹ️ Task Manager opened as prerequisite for containment step`);
        } else {
          isCorrect = false;
          shouldProgress = false;
        }
      }
      // Special handling: observe_and_scan - user needs to observe system symptoms and check for lateral movement
      // This step is more exploratory: opening Task Manager, File Explorer, or viewing network scan results are all valid
      else if (expectedAction === 'observe_and_scan') {
        const observationActions = [
          'open_taskmgr',
          'open_file_explorer',
          'view_network_scan'
        ];
        const observationEvents = [
          'taskmgr_opened',
          'task_manager_opened',
          'file_explorer_opened',
          'folder_opened',
          'network_scan_viewed'
        ];

        if (observationActions.includes(action) || observationEvents.includes(stepEvent)) {
          isCorrect = true;
          shouldProgress = autoAdvance !== false;
          console.log(`✅ Observation/scan action recognized for observe_and_scan step:`, { action, stepEvent });
        } else {
          // Other random actions during this step don't count as incorrect; they simply don't progress the step
          isCorrect = false;
          shouldProgress = false;
        }
      }
      // Special handling: extract_and_execute can be completed by executing a file
      // This allows steps that expect extraction+execution to be completed by just executing the extracted file
      else if (expectedAction === 'extract_and_execute' && (action === 'execute_file' || stepEvent === 'file_executed')) {
        // Check if the executed file matches what should be extracted (Invoice_7823.docx.exe)
        const expectedFileName = 'Invoice_7823.docx.exe';
        const executedFileName = dataPayload?.name || '';
        if (executedFileName.toLowerCase().includes('invoice_7823') && executedFileName.toLowerCase().includes('.exe')) {
          isCorrect = true;
          shouldProgress = autoAdvance !== false;
          console.log(`✅ File execution recognized for extract_and_execute step: ${executedFileName}`);

          // Show notification if step has content.notify
          if (currentStep.content?.notify) {
            const notifyConfig = currentStep.content.notify;
            // The notification will be shown by RealisticOSSimulator when the process is spawned
            // But we can also trigger it here if needed
            console.log('[EnhancedMalware] Step has notify config:', notifyConfig);
          }
        } else {
          isCorrect = false;
          shouldProgress = false;
        }
      }
      // Check if action matches expected action (STRICT - exact match required)
      else if (expectedAction && action === expectedAction) {
        isCorrect = true;

        // Special handling: If step has initial_path and user navigated to it, complete even if autoAdvance is false
        const expectedPath = currentStep.content?.initial_path || currentStep.content?.initialPath;
        if (expectedPath && dataPayload?.path && stepEvent === 'folder_opened') {
          // User navigated to expected folder - complete the step
          // Normalize paths and handle common typos (e.g., "Aderson" -> "Anderson")
          let normalizedExpected = normalizePathUtil(expectedPath);
          let normalizedActual = normalizePathUtil(dataPayload.path);

          // Fix common typos in path comparison
          normalizedExpected = normalizedExpected.replace(/John Aderson/gi, 'John Anderson');
          normalizedActual = normalizedActual.replace(/John Aderson/gi, 'John Anderson');

          // Compare paths (case-insensitive, handle both directions)
          const expectedLower = normalizedExpected.toLowerCase().replace(/[\\/]/g, '\\');
          const actualLower = normalizedActual.toLowerCase().replace(/[\\/]/g, '\\');

          if (expectedLower === actualLower ||
            actualLower.endsWith(expectedLower) ||
            expectedLower.endsWith(actualLower) ||
            actualLower.includes(expectedLower.split('\\').pop() || '') ||
            expectedLower.includes(actualLower.split('\\').pop() || '')) {
            shouldProgress = true; // Force progress when navigating to expected folder
            console.log(`✅ User navigated to expected folder '${expectedPath}' (actual: '${dataPayload.path}') - completing step`);
          } else {
            shouldProgress = autoAdvance !== false; // Default to true unless explicitly false
          }
        }
        // Special handling: For open_file_explorer action, if step has initial_path but user just opened explorer (not navigated yet),
        // still complete the step if autoAdvance is not explicitly false
        else if (expectedAction === 'open_file_explorer' && expectedPath && stepEvent === 'file_explorer_opened') {
          // User opened File Explorer - complete step immediately (don't require navigation)
          shouldProgress = true;
          console.log(`✅ File Explorer opened for step with initial_path - completing step immediately`);
        }
        else {
          // Progress if action matches and autoAdvance is enabled
          shouldProgress = autoAdvance !== false; // Default to true unless explicitly false
        }
        console.log(`✅ Action '${action}' matches expected action '${expectedAction}' for step: ${currentStep.title}`);
      }
      // Check if event matches expected event (only if action didn't match)
      else if (!isCorrect && expectedEvent && stepEvent && expectedEvent === stepEvent) {
        isCorrect = true;
        shouldProgress = autoAdvance !== false;
      }
      // Check if this is a prerequisite action (allowed but doesn't progress)
      else if (expectedAction && prerequisiteActions[expectedAction]) {
        const prereqs = prerequisiteActions[expectedAction];
        const isPrerequisite = prereqs.includes(action) || prereqs.includes(stepEvent);

        if (isPrerequisite) {
          // Prerequisite action - don't mark as incorrect, but don't progress either
          isCorrect = true; // Mark as correct so it doesn't show "Incorrect Action!"
          shouldProgress = false; // Don't progress - wait for the actual action
          console.log(`ℹ️ Prerequisite action '${action}' performed for step requiring '${expectedAction}'. Waiting for final action.`);
        }
      }
      // If no expected action/event is defined, check step description/hints for implied actions
      else if (!expectedAction && !expectedEvent) {
        // Only use implied actions if no explicit action is defined
        const stepText = `${currentStep.title || ''} ${currentStep.description || ''} ${(currentStep.hints || []).join(' ')}`.toLowerCase();

        // Map common implied actions based on step text
        if (stepText.includes('task manager') || stepText.includes('taskmgr') || stepText.includes('process')) {
          if (action === 'open_taskmgr' || stepEvent === 'taskmgr_opened' || stepEvent === 'task_manager_opened') {
            isCorrect = true;
            shouldProgress = autoAdvance !== false;
          }
        } else if (stepText.includes('file explorer') || stepText.includes('file') && stepText.includes('folder')) {
          if (action === 'open_file_explorer' || stepEvent === 'file_explorer_opened') {
            isCorrect = true;
            shouldProgress = autoAdvance !== false;
          }
        } else if (stepText.includes('outlook') || stepText.includes('email') || stepText.includes('inbox')) {
          if (action === 'open_outlook' || stepEvent === 'outlook_opened') {
            isCorrect = true;
            shouldProgress = autoAdvance !== false;
          }
        } else if (stepText.includes('enable content') || stepText.includes('enable macros') || stepText.includes('enabling macros') || stepText.includes('macro')) {
          if (action === 'enable_macros' || stepEvent === 'macro_enabled') {
            isCorrect = true;
            shouldProgress = autoAdvance !== false;
            console.log('[EnhancedMalware] Recognized enable macros action for step');
          }
        } else if (stepText.includes('containment') || stepText.includes('terminate') || stepText.includes('stop processes')) {
          // For containment steps mentioned in text
          if (action === 'end_task' || stepEvent === 'process_terminated') {
            isCorrect = true;
            shouldProgress = false; // Need to quarantine files after
            console.log('[EnhancedMalware] Recognized process termination for containment step');
          } else if (action === 'quarantine_file' || stepEvent === 'file_quarantined') {
            isCorrect = true;
            shouldProgress = true; // Complete after quarantine
            console.log('[EnhancedMalware] Recognized file quarantine for containment step');
          }
        }
      }

      // If action doesn't match and no implied action matches, it's incorrect
      if (!isCorrect) {
        console.log(`❌ Action '${action}' (event: '${stepEvent}') does not match expected action '${expectedAction}' or event '${expectedEvent}' for step: ${currentStep.title}`);
      } else if (shouldProgress) {
        console.log(`✅ Action '${action}' (event: '${stepEvent}') matches expected action '${expectedAction}' or event '${expectedEvent}' for step: ${currentStep.title}`);
      }
    } else if (currentStep.type === 'identification') {
      // Handle identification steps - user needs to identify/find something
      const expectedAction = currentStep.content?.action ||
        currentStep.content?.correctAction;
      const expectedEvent = currentStep.content?.expectedEvent || currentStep.content?.event;
      const autoAdvance = currentStep.content?.autoAdvance;

      console.log('[EnhancedMalware] Identification step validation:', {
        stepTitle: currentStep.title,
        action,
        stepEvent,
        expectedAction,
        expectedEvent,
        content: currentStep.content
      });

      // For identification steps, navigating to the location and viewing it counts as identification
      // Example: navigating to extensions page = identifying the extension
      if (expectedAction === 'identify_extension') {
        // Opening extensions page or navigating to extensions counts as identifying
        if (action === 'navigate_to_extensions' ||
          action === 'identify_extension' ||
          stepEvent === 'browser_extensions_opened') {
          isCorrect = true;
          shouldProgress = autoAdvance !== false; // Default to true unless explicitly false
          console.log(`✅ Extension identification recognized for step: ${currentStep.title}`);
        }
      }
      // Generic identification - check if action matches
      else if (expectedAction && action === expectedAction) {
        isCorrect = true;
        shouldProgress = autoAdvance !== false;
        console.log(`✅ Action '${action}' matches expected action '${expectedAction}' for identification step: ${currentStep.title}`);
      }
      // Check if event matches expected event
      else if (expectedEvent && stepEvent && expectedEvent === stepEvent) {
        isCorrect = true;
        shouldProgress = autoAdvance !== false;
      }
    } else if (currentStep.type === 'knowledge_check') {
      // Knowledge checks are handled separately
      return;
    } else if (currentStep.type === 'decision_making') {
      // Decision making steps are typically knowledge checks with multiple choice
      // They should be handled similar to knowledge_check
      return;
    }

    const decision = {
      stepId: stepIdToUse,
      action,
      event: stepEvent,
      payload: dataPayload,
      correct: isCorrect,
      timestamp: Date.now(),
      timeSpent: timeElapsed
    };

    // Use functional updater to avoid stale closure on userDecisions
    setUserDecisions(prev => {
      const next = {
        ...prev,
        [stepIdToUse]: decision
      };

      // Update progress using merged decisions immediately
      const completed = Object.keys(next).length;
      const correctDecisions = Object.values(next).filter(d => d.correct).length;
      setProgress({
        completed,
        total: steps.length,
        accuracy: Math.round((correctDecisions / Math.max(1, completed)) * 100)
      });

      return next;
    });

    // Determine feedback message based on action type
    let feedbackTitle = isCorrect ? 'Correct Action!' : 'Incorrect Action!';
    let feedbackExplanation = '';

    if (!isCorrect) {
      feedbackExplanation = 'That action is not appropriate for this step. Try a different approach.';
    } else if (shouldProgress) {
      feedbackExplanation = 'Great! You took the correct action for this step.';
    } else {
      // Prerequisite action - provide helpful guidance
      const expectedAction = currentStep.content?.action;
      if (expectedAction === 'quarantine_file') {
        feedbackTitle = 'File Explorer Opened';
        feedbackExplanation = 'Good! Now navigate to the file and quarantine it to complete this step.';
      } else if (expectedAction === 'end_task') {
        feedbackTitle = 'Task Manager Opened';
        feedbackExplanation = 'Good! Now find and end the suspicious process to complete this step.';
      } else if (expectedAction === 'execute_file' || expectedAction === 'extract_and_execute') {
        feedbackTitle = 'File Explorer Opened';
        feedbackExplanation = 'Good! Now locate and execute the file to complete this step.';
      } else {
        feedbackTitle = 'Correct Action!';
        feedbackExplanation = 'Good! Continue with the next required action.';
      }
    }

    // Show feedback
    setFeedbackContent({
      isCorrect,
      title: feedbackTitle,
      explanation: feedbackExplanation,
      action: currentStep.content?.action,
      stepTitle: currentStep.title,
      shouldProgress
    });

    // Show feedback and progress if action was correct and should progress
    if (shouldProgress && isCorrect) {
      // Double-check we're still on the same step (prevent race conditions)
      const stepIdAtTime = steps[currentStepIndex]?.id || `step-${currentStepIndex}`;
      const eventKey = `${stepIdAtTime}-${action}`;

      // Prevent duplicate completions
      if (processedEventsRef.current.has(eventKey)) {
        console.log("⚠️ Duplicate action prevented:", eventKey);
        return;
      }

      setShowFeedback(true);
      setTimeout(() => {
        setShowFeedback(false);
        setFeedbackContent(null);

        // Complete current step and advance
        handleStepComplete({
          action,
          event: stepEvent,
          payload: dataPayload,
          correct: isCorrect
        });
      }, 1500);
    } else if (isCorrect && !shouldProgress) {
      // Prerequisite action - show feedback but don't progress
      setShowFeedback(true);
      // Auto-hide after 2 seconds for prerequisite actions
      setTimeout(() => {
        setShowFeedback(false);
        setFeedbackContent(null);
      }, 2000);
    } else {
      // Show feedback for incorrect actions (but don't progress)
      setShowFeedback(true);
    }
  }, [steps, currentStepIndex, timeElapsed, completeChallenge]);

  // Handle knowledge check answers
  const handleAnswer = useCallback((answerIndex, stepId) => {
    const currentStep = steps[currentStepIndex];
    if (!currentStep) return;

    const stepIdToUse = stepId || currentStep.id;
    const isCorrect = answerIndex === currentStep.content?.correctAnswer;

    const decision = {
      stepId: stepIdToUse,
      answer: answerIndex,
      correct: isCorrect,
      timestamp: Date.now(),
      timeSpent: timeElapsed
    };

    setUserDecisions(prev => {
      const next = {
        ...prev,
        [stepIdToUse]: decision
      };

      const completed = Object.keys(next).length;
      const correctDecisions = Object.values(next).filter(d => d.correct).length;

      setProgress({
        completed,
        total: steps.length,
        accuracy: Math.round((correctDecisions / Math.max(1, completed)) * 100)
      });

      return next;
    });

    // Show feedback
    setFeedbackContent({
      isCorrect,
      title: isCorrect ? 'Correct Answer!' : 'Incorrect Answer!',
      explanation: currentStep.content?.explanation,
      selectedAnswer: currentStep.content?.options?.[answerIndex],
      correctAnswer: currentStep.content?.options?.[currentStep.content?.correctAnswer],
      stepTitle: currentStep.title,
      shouldProgress: isCorrect
    });

    // Auto-progress if the answer was correct
    if (isCorrect) {
      setShowFeedback(true);
      setTimeout(() => {
        setShowFeedback(false);
        setFeedbackContent(null);

        if (currentStepIndex < steps.length - 1) {
          setCurrentStepIndex(currentStepIndex + 1);
        } else {
          completeChallenge();
        }
      }, 1500);
    } else {
      setShowFeedback(true);
    }
  }, [steps, currentStepIndex, timeElapsed, completeChallenge]);

  // Handle next step manually
  const handleNextStep = useCallback(() => {
    setShowFeedback(false);
    setFeedbackContent(null);

    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      completeChallenge();
    }
  }, [currentStepIndex, steps.length, completeChallenge]);

  useEffect(() => {
    if (steps.length > 0 && currentStepIndex >= steps.length && !isCompleted) {
      const correctDecisions = Object.values(userDecisions).filter(d => d.correct).length;
      const summary = {
        correct: correctDecisions,
        total: steps.length,
        score: Math.round((correctDecisions / steps.length) * 100),
        timeElapsed,
        decisions: userDecisions,
        accuracy: Math.round((correctDecisions / Math.max(Object.keys(userDecisions).length, 1)) * 100)
      };

      setIsCompleted(true);
      if (onComplete) {
        onComplete(summary);  // ✅ Notify container to show completion screen
      }
    }
  }, [currentStepIndex, steps, isCompleted, userDecisions, onComplete, timeElapsed]);

  // Handle learn more
  const handleLearnMore = useCallback(() => {
    if (challenge?.metadata) {
      window.open(challenge.metadata, '_blank', 'noopener,noreferrer');
    } else {
      const arenaId = scenario?.arenaId || 'malware_ransomware';
      if (arenaId) {
        navigate(`/learning/module/${arenaId}?topic=${arenaId}`);
      }
    }
  }, [challenge, scenario, navigate]);

  // Start simulation
  const startSimulation = useCallback(() => {
    setSimulationStarted(true);
    // Force show step prompt when simulation starts
    if (steps && steps.length > 0) {
      setShowStepPrompt(true);
    }
  }, [steps]);

  const closeStepPrompt = useCallback(() => {
    // Smooth fade out before unmounting
    setStepPromptAnimVisible(false);
    setTimeout(() => setShowStepPrompt(false), 200);
  }, []);


  // Render per-step prompt modal (single-question/step view)
  const renderStepPromptModal = () => {
    if (!steps[currentStepIndex]) return null;
    if (!showStepPrompt) return null; // separate checks for clarity


    const step = steps[currentStepIndex];
    const options = Array.isArray(step.content?.options) ? step.content.options : [];
    const isQuestion = step.type === 'knowledge_check' || step.type === 'decision_making' || step.type === 'identification';

    return (
      <div className={`fixed inset-0 flex items-center justify-end z-[10005] p-4 pr-16 transition-opacity duration-200 ${stepPromptAnimVisible ? 'bg-black/50 opacity-100' : 'bg-black/30 opacity-0'} pointer-events-none`}>
        <div className={`bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 transition-all duration-200 ease-out ${stepPromptAnimVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-2 scale-[0.98]'} pointer-events-auto`}>
          <div className="mb-1 text-xs text-gray-500">Step {currentStepIndex + 1} / {steps.length}</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
          <p className="text-sm text-gray-700 mb-4">{step.description}</p>

          {/* Hints */}
          {step.hints && step.hints.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <div className="flex items-center mb-1">
                <AlertTriangle size={16} className="text-yellow-600 mr-2" />
                <span className="text-sm font-medium text-yellow-800">Hint</span>
              </div>
              <p className="text-sm text-yellow-700">{step.hints[0]}</p>
            </div>
          )}

          {/* Options for knowledge checks */}
          {isQuestion ? (
            <div className="space-y-2 mb-4">
              {options.map((opt, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    // Answer inside the modal
                    closeStepPrompt();
                    handleAnswer(idx, step.id);
                  }}
                  className="w-full p-3 text-left bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors text-gray-900"
                >
                  {opt}
                </button>
              ))}
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-800">
              Complete this action in the simulator on the left.
            </div>
          )}

          <div className="flex justify-end space-x-2">
            {!isQuestion && (
              <button
                onClick={closeStepPrompt}
                className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Start Step
              </button>
            )}
            {isQuestion && (
              <button
                onClick={closeStepPrompt}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render feedback modal
  const renderFeedbackModal = () => {
    if (!showFeedback || !feedbackContent) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10005] p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
          <div className="flex items-center mb-4">
            {feedbackContent.isCorrect ? (
              <CheckCircle size={24} className="text-green-600 mr-3" />
            ) : (
              <XCircle size={24} className="text-red-600 mr-3" />
            )}
            <h3 className={`text-lg font-semibold ${feedbackContent.isCorrect ? 'text-green-800' : 'text-red-800'
              }`}>
              {feedbackContent.title}
            </h3>
          </div>

          <p className="text-gray-700 mb-4">{feedbackContent.explanation}</p>

          {feedbackContent.selectedAnswer && (
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="text-sm text-gray-600">
                <strong>Your answer:</strong> {feedbackContent.selectedAnswer}
              </p>
              {!feedbackContent.isCorrect && (
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Correct answer:</strong> {feedbackContent.correctAnswer}
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            {/* <button
              onClick={handleLearnMore}
              className="flex items-center px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors"
            >
              <BookOpen size={16} className="mr-2" />
              Learn More
            </button> */}
            <button
              onClick={handleNextStep}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {feedbackContent?.shouldProgress && feedbackContent?.isCorrect ?
                (currentStepIndex < steps.length - 1 ? 'Continue' : 'Complete Challenge') :
                'Next Step'
              }
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render loading state
  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold mb-2">Loading Challenge</h2>
          <p className="text-blue-200">Preparing your security simulation...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-red-900 via-red-800 to-purple-900 flex items-center justify-center">
        <div className="text-center text-white max-w-md mx-4">
          <XCircle size={64} className="text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Error Loading Challenge</h2>
          <p className="text-red-200 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Render completion state
  if (isCompleted) {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-green-900 via-green-800 to-blue-900 flex items-center justify-center">
        <div className="text-center text-white max-w-md mx-4">
          <CheckCircle size={64} className="text-green-400 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Challenge Complete!</h2>
          <p className="text-green-200 mb-6">
            You scored {progress.accuracy}% accuracy in {Math.floor(timeElapsed / 60)}:{(timeElapsed % 60).toString().padStart(2, '0')}
          </p>
          <div className="space-y-3">
            <button
              onClick={handleLearnMore}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors"
            >
              <BookOpen size={16} />
              Learn More
            </button>
            <button
              onClick={onExit}
              className="w-full bg-gray-600 text-white py-2 rounded hover:bg-gray-700 transition-colors"
            >
              Exit
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render main simulator
  return (
    <div className="fixed inset-0 w-full h-full overflow-hidden">
      {/* Full Screen OS Simulator */}
      <div className="w-full h-full">
        <RealisticOSSimulator
          osType="windows11"
          fullScreen={true}
          challenge={challenge}
          scenarios={scenarios}
          scenario={scenario}
          activeStep={steps[currentStepIndex] || null}
          onComplete={(event, payload) => {
            console.log("🧭 OS event:", event, payload);

            // Map OS/app-level events to step actions
            // handleUserAction will validate and only progress if action matches step requirement
            switch (event) {
              case 'email_deleted':
                handleUserAction('delete_email', 'email_deleted', payload);
                break;
              case 'outlook_opened':
                handleUserAction('open_outlook', 'outlook_opened', payload);
                break;
              case 'file_explorer_opened':
              case 'open_file_explorer':
                handleUserAction('open_file_explorer', 'file_explorer_opened', payload);
                break;
              case 'folder_opened':
                // Handle folder navigation - check if it matches expected initial_path
                const currentStepForFolder = steps[currentStepIndex];
                const expectedPath = currentStepForFolder?.content?.initial_path ||
                  currentStepForFolder?.content?.initialPath;

                if (expectedPath && payload?.path) {
                  // Normalize both paths for comparison
                  let normalizedExpected = normalizePathUtil(expectedPath);
                  let normalizedActual = normalizePathUtil(payload.path);

                  // Fix common typos in path comparison (e.g., "Aderson" -> "Anderson")
                  normalizedExpected = normalizedExpected.replace(/John Aderson/gi, 'John Anderson');
                  normalizedActual = normalizedActual.replace(/John Aderson/gi, 'John Anderson');

                  // Compare paths (case-insensitive, handle both directions)
                  const expectedLower = normalizedExpected.toLowerCase().replace(/[\\/]/g, '\\');
                  const actualLower = normalizedActual.toLowerCase().replace(/[\\/]/g, '\\');

                  // Check if user navigated to the expected folder
                  if (expectedLower === actualLower ||
                    actualLower.endsWith(expectedLower) ||
                    expectedLower.endsWith(actualLower) ||
                    actualLower.includes(expectedLower.split('\\').pop() || '') ||
                    expectedLower.includes(actualLower.split('\\').pop() || '')) {
                    // User navigated to expected folder - complete the step if it expects open_file_explorer
                    if (currentStepForFolder?.content?.action === 'open_file_explorer') {
                      handleUserAction('open_file_explorer', 'folder_opened', payload);
                    } else {
                      handleUserAction('open_folder', 'folder_opened', payload);
                    }
                  } else {
                    // Just folder navigation, not to expected path
                    handleUserAction('open_folder', 'folder_opened', payload);
                  }
                } else {
                  // No expected path, just handle as folder navigation
                  handleUserAction('open_folder', 'folder_opened', payload);
                }
                break;
              case 'taskmgr_opened':
              case 'task_manager_opened':
                handleUserAction('open_taskmgr', 'taskmgr_opened', payload);
                break;
              case 'file_executed':
              case 'process_started':
                handleUserAction('execute_file', 'file_executed', payload);
                break;
              case 'process_terminated':
              case 'taskmgr_process_ended':
                handleUserAction('end_task', 'process_terminated', payload);
                break;
              case 'file_quarantined':
              case 'file_deleted':
                // For containment steps, quarantine completes the step after processes are terminated
                const currentStepForQuarantine = steps[currentStepIndex];
                const isContainmentQuarantine = currentStepForQuarantine?.content?.action === 'contain_and_collect' ||
                  currentStepForQuarantine?.content?.action === 'contain_recover' ||
                  currentStepForQuarantine?.title?.toLowerCase().includes('containment');

                handleUserAction('quarantine_file', 'file_quarantined', payload);

                // For containment steps, auto-complete after quarantining (processes should already be terminated)
                if (isContainmentQuarantine) {
                  console.log('[EnhancedMalware] Containment step - file quarantined, completing step');
                  setTimeout(() => {
                    handleStepComplete({
                      event: 'file_quarantined',
                      payload: { ...payload, containAndCollect: true },
                      success: true
                    });
                  }, 1000);
                }
                break;
              case 'ads_quarantined': {
                // ADS quarantine from File Explorer (hidden stream)
                // Map to quarantine_ads for steps that explicitly expect ADS quarantine
                const action = 'quarantine_ads';
                const currentStep = steps[currentStepIndex];
                const augmentedPayload = payload ? { ...payload } : {};

                // If processTerminated is false/undefined, mark as incorrect attempt
                if (augmentedPayload.processTerminated === false) {
                  augmentedPayload.isIncorrect = true;
                }

                handleUserAction(action, 'ads_quarantined', augmentedPayload);
                break;
              }
              case 'command_executed':
                // Handle command execution from Command Prompt
                // This will be processed by handleUserAction which recognizes toolCommand steps
                handleUserAction('run_command', 'command_executed', payload);
                break;
              case 'macro_enabled':
                // Handle macro enabled event from File Explorer
                // Use handleUserAction to process and validate - it will check for onUserAction, expectedAction, or implied actions
                handleUserAction('enable_macros', 'macro_enabled', payload);
                break;
              case 'browser_opened':
                // Handle browser opened event from ChromeBrowserContent
                handleUserAction('open_browser', 'browser_opened', payload);
                break;
              case 'browser_extensions_opened':
                // Handle browser extensions page opened event
                // For identification steps expecting identify_extension, this counts as identification
                // For other steps, it's navigate_to_extensions
                const currentStepForExtensions = steps[currentStepIndex];
                if (currentStepForExtensions?.type === 'identification' &&
                  currentStepForExtensions?.content?.action === 'identify_extension') {
                  handleUserAction('identify_extension', 'browser_extensions_opened', payload);
                } else {
                  handleUserAction('navigate_to_extensions', 'browser_extensions_opened', payload);
                }
                break;
              case 'extension_removed':
                // Handle extension removal event from ChromeBrowserContent
                handleUserAction('remove_extension', 'extension_removed', payload);
                break;
              case 'all_malicious_extensions_removed':
                // Handle all malicious extensions removed event (legacy)
                const currentStepForRemoval = steps[currentStepIndex];
                if (currentStepForRemoval?.content?.action === 'remove_extension') {
                  handleUserAction('remove_extension', 'all_malicious_extensions_removed', payload);
                }
                break;
              case 'file_extracted':
                // Handle ZIP file extraction - track extraction for extract_and_execute steps
                console.log('[EnhancedMalware] File extracted:', payload);
                const currentStepForExtraction = steps[currentStepIndex];
                if (currentStepForExtraction?.content?.onUserAction === 'extract_and_execute' && payload?.extractedFile) {
                  // File extracted, now user can execute it
                  console.log('[EnhancedMalware] Extracted file ready for execution:', payload.extractedFile.name);
                }
                break;
              default:
                console.log("ℹ️ Unhandled OS event:", event);
                break;
            }
            // Note: handleUserAction handles step completion internally if action is correct
          }}
          onExit={onExit}
          onExecuteFile={(file) => {
            if (file.processConfig) {
              // Check if current step has a notification to show
              const currentStep = steps[currentStepIndex];
              if (currentStep?.content?.notify) {
                const notifyConfig = currentStep.content.notify;
                // Trigger notification via RealisticOSSimulator's addNotification
                // The notification will be shown when the process spawns
                console.log('[EnhancedMalware] File execution should trigger notification:', notifyConfig);
              }
              handleUserAction('execute_file', 'file_executed', file);
            }
          }}
          onOpenOutlook={() => {
            handleUserAction('open_outlook', 'outlook_opened');
          }}
          onOpenTaskManager={() => {
            // Special handling: in the Polymorphic Virus challenge, Task Manager auto-opens
            // after Notepad to show the rogue process. During the "Open Notepad" step,
            // this should NOT be treated as a user action to avoid incorrect feedback.
            const currentStep = steps[currentStepIndex];
            const challengeId = (challenge && (challenge.id || challenge.challenge_id || challenge._id)) || null;

            if (
              challengeId === '066306f2-2fb1-4b7f-bb81-e6f7b837975f' && // Polymorphic Virus
              currentStep &&
              (currentStep.content?.action === 'open_notepad' ||
                (currentStep.title || '').toLowerCase().includes('open notepad'))
            ) {
              console.log('[EnhancedMalware] Ignoring auto-opened Task Manager during Open Notepad step');
              return;
            }

            handleUserAction('open_taskmgr', 'taskmgr_opened');
          }}
          onOpenNotepad={(windowInfo) => {
            // Treat opening Notepad as the primary action for the polymorphic virus scenario
            handleUserAction('open_notepad', 'notepad_opened', windowInfo || {});
          }}
          onEndProcess={(processId, processName) => {
            handleUserAction('end_task', 'process_terminated', { processId, processName });
          }}
          onOpenFolder={(folder) => handleUserAction('open_folder', 'folder-navigation', folder)}
        />
      </div>


      {/* Step Prompt Modal */}
      {renderStepPromptModal()}

      {/* Debug: Force show step prompt button */}
      {steps && steps.length > 0 && !showStepPrompt && (
        <div className="fixed top-4 right-4 z-50">
          <button
            onClick={() => setShowStepPrompt(true)}
            className="bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-red-700"
          >
            Show Step Prompt
          </button>
        </div>
      )}

      {/* Feedback Modal */}
      {renderFeedbackModal()}
    </div>
  );
};

export default EnhancedMalwareSimulator;